import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";

// Fully integrated API call inside the slice
export const fetchCashoutAdmin = createAsyncThunk(
  "cashoutAdmin/fetchCashoutAdmin",
  async (
    { pageId = 1, pageSize = 10, searchText = "" } = {},
    { rejectWithValue },
  ) => {
    try {
      const query = new URLSearchParams({
        pageId: pageId.toString(),
        pageSize: pageSize.toString(),
        ...(searchText && { searchText }),
      }).toString();

      const response = await axiosClient.get(`order/cashoutAdmin?${query}`);

      if (response.data.statusCode === 200) {
        return {
          vendors: response.data.Data || [],
          total: Number(response.data.pagination.totalRecords) || 0,
        };
      } else {
        throw new Error(response.data.msg || "Failed to fetch vendors");
      }
    } catch (error) {
      console.error("Error fetching cashout admin:", error);
      return rejectWithValue(error.message || "Network error");
    }
  },
);

// In CashoutAdminSlice.js – replace the thunk

export const fetchAdminPayToProviderList = createAsyncThunk(
  "cashoutAdmin/fetchAdminPayToProviderList",
  async (
    {
      id,
      pageId = 1,
      pageSize = 10,
      searchTextAdminPay = "",
      fromDate = "",
      toDate = "",
    } = {},
    { rejectWithValue },
  ) => {
    if (!id) return rejectWithValue("Vendor ID is required");

    try {
      const query = new URLSearchParams({
        pageId: pageId.toString(),
        pageSize: pageSize.toString(),
        searchText: searchTextAdminPay.trim() || "", // always send, even empty
        fromDate: fromDate.trim() || "", // empty = no filter
        toDate: toDate.trim() || "",
      }).toString();

      const url = `order/AdminPayToProviderList/${id}${query ? `?${query}` : ""}`;

      console.log("Fetching payments → URL:", url); // debug

      const response = await axiosClient.get(url);

      console.log("Payments response:", response.data); // debug

      if (response.data.statusCode === 200) {
        return {
          type: "payments",
          payments: response.data.Data || [],
          total: Number(response.data.pagination?.totalRecords) || 0,
        };
      } else {
        throw new Error(
          response.data.msg || response.data.message || "API error",
        );
      }
    } catch (error) {
      console.error("Payments fetch failed:", error.response?.data || error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.msg ||
          error.message ||
          "Failed to load payment history",
      );
    }
  },
);

export const fetchPaypalHistoryByReceiverId = createAsyncThunk(
  "cashoutAdmin/fetchPaypalHistoryByReceiverId",
  async (
    {
      id,
      pageId = 1,
      pageSize = 10,
      searchText = "",
      fromDate = "",
      toDate = "",
    } = {},
    { rejectWithValue },
  ) => {
    if (!id) return rejectWithValue("Receiver ID is required");

    try {
      const query = new URLSearchParams({
        pageId: pageId.toString(),
        pageSize: pageSize.toString(),
        ...(searchText && { searchText }),
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
      }).toString();

      const url = `order/paypalHistoryByReceiverId/${id}${query ? `?${query}` : ""}`;

      const response = await axiosClient.put(url);

      if (response.data.statusCode === 200) {
        return {
          paypalHistory: response.data.Data || [],
          total: Number(response.data.pagination?.totalRecords) || 0,
        };
      } else {
        throw new Error(response.data.msg || "Failed to fetch paypal history");
      }
    } catch (error) {
      return rejectWithValue(error.message || "Network error");
    }
  },
);

const initialState = {
  vendors: [],
  payments: [],
  paypalHistory: [],
  totalRecords: 0,
  currentPage: 1,
  pageSize: 10,
  loading: false,
  error: null,
  searchText: "",
  lastVendorId: null,
  searchTextAdminPay: "",
  currentPageAdminPay: 1,
  totalRecordsAdminPay: 0,
  pageSizeAdminPay: 10,
  currentPagePaypal: 1,
  totalRecordsPaypal: 0,
  pageSizePaypal: 10,
};

const CashoutAdminSlice = createSlice({
  name: "cashoutAdmin",
  initialState,
  reducers: {
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setSearchText(state, action) {
      state.searchText = action.payload;
    },
    setSearchTextAdminPay(state, action) {
      state.searchTextAdminPay = action.payload;
    },
    setCurrentPageAdminPay(state, action) {
      state.currentPageAdminPay = action.payload;
    },
    setTotalRecordsAdminPay(state, action) {
      state.totalRecordsAdminPay = action.payload;
    },
    setCurrentPagePaypal(state, action) {
      state.currentPagePaypal = action.payload;
    },
    setTotalRecordsPaypal(state, action) {
      state.totalRecordsPaypal = action.payload;
    },
    resetCashoutState(state) {
      return {
        ...initialState,
        currentPage: 1,
      };
    },
    resetPayments(state) {
      state.payments = [];
      state.lastVendorId = null;
      state.searchTextAdminPay = "";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchCashoutAdmin (vendor list)
      .addCase(fetchCashoutAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCashoutAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload.vendors;
        state.totalRecords = action.payload.total;
        state.searchText = action.meta.arg.searchText || "";
      })
      .addCase(fetchCashoutAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load vendors";
        state.vendors = [];
      })

      // fetchAdminPayToProviderList (single vendor payments)
      .addCase(fetchAdminPayToProviderList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminPayToProviderList.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload.payments;
        state.totalRecordsAdminPay = action.payload.total; // ← NEW
        state.currentPageAdminPay = action.meta.arg.pageId; // ← NEW (sync current page)
        state.lastVendorId = action.meta.arg.id;
        state.searchTextAdminPay = action.meta.arg.searchText || "";
      })
      .addCase(fetchAdminPayToProviderList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load payment history";
        state.payments = [];
      })
      .addCase(fetchPaypalHistoryByReceiverId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaypalHistoryByReceiverId.fulfilled, (state, action) => {
        state.loading = false;
        state.paypalHistory = action.payload.paypalHistory;
        state.totalRecordsPaypal = action.payload.total;
        state.currentPagePaypal = action.meta.arg.pageId;
      })
      .addCase(fetchPaypalHistoryByReceiverId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load paypal history";
        state.paypalHistory = [];
      });
  },
});

export const {
  setCurrentPage,
  setSearchText,
  setSearchTextAdminPay,
  resetCashoutState,
  setCurrentPageAdminPay,
  setTotalRecordsAdminPay,
  resetPayments,
  setCurrentPagePaypal,
  setTotalRecordsPaypal,
} = CashoutAdminSlice.actions;

export default CashoutAdminSlice.reducer;
